import { motion } from 'framer-motion'
import { Briefcase, Code2, GraduationCap } from 'lucide-react'
import { experiences } from '../../data/experience'

const Experience = () => {
  const getIcon = (type: string) => {
    const icons = {
      work: Briefcase,
      opensource: Code2,
      education: GraduationCap
    }
    return icons[type as keyof typeof icons] || Briefcase
  }

  return (
    <section className="section section-border">
      <div className="container-tight">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mb-16 max-w-2xl"
        >
          <span className="section-label mb-4">Experience</span>
          <h2 className="text-balance mb-6">My professional journey.</h2>
          <p className="text-lg text-zinc-500 dark:text-zinc-400">
            A path shaped by enterprise engineering, open-source collaboration, and a commitment to building secure, scalable systems.
          </p>
        </motion.div>

        <div className="relative">
          <div className="absolute left-[11px] top-3 bottom-3 hidden w-px bg-zinc-200 dark:bg-zinc-800 sm:block" />

          <div className="space-y-10">
            {experiences.map((experience, index) => {
              const Icon = getIcon(experience.type)

              return (
                <motion.div
                  key={experience.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: index * 0.05, ease: [0, 0, 0.2, 1] }}
                  viewport={{ once: true }}
                  className="relative sm:pl-10"
                >
                  <div className="absolute left-0 top-1 hidden h-6 w-6 items-center justify-center rounded-full border border-zinc-200 bg-white sm:flex dark:border-zinc-800 dark:bg-canvas-dark">
                    <div className="h-2 w-2 rounded-full bg-cyan-500" />
                  </div>

                  <div className="card card-hover">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-900">
                          <Icon className="h-4 w-4 text-cyan-500" strokeWidth={1.5} />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                            {experience.title}
                          </h3>
                          <p className="text-sm text-zinc-500 dark:text-zinc-400">
                            {experience.company} · {experience.location}
                          </p>
                        </div>
                      </div>
                      <div className="font-mono text-[11px] font-medium uppercase tracking-widest text-zinc-400">
                        {experience.duration}
                      </div>
                    </div>

                    <ul className="mb-5 space-y-2">
                      {experience.description.map((desc, descIndex) => (
                        <li
                          key={descIndex}
                          className="flex items-start gap-3 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400"
                        >
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-zinc-400" />
                          <span>{desc}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-wrap gap-2">
                      {experience.technologies.map((tech) => (
                        <span key={tech} className="tag">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mt-20 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-200 dark:border-zinc-800 dark:bg-zinc-800 sm:grid-cols-3"
        >
          {[
            { label: 'Years Experience', value: `${experiences.filter(e => e.type === 'work').length}+` },
            { label: 'Open Source Contributions', value: experiences.filter(e => e.type === 'opensource').length.toString() },
            { label: 'Educational Milestones', value: experiences.filter(e => e.type === 'education').length.toString() }
          ].map((stat) => (
            <div key={stat.label} className="bg-canvas p-6 text-center dark:bg-canvas-dark">
              <div className="font-mono text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                {stat.value}
              </div>
              <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default Experience
